import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "@/core/components/ui/button";
import { Input } from "@/core/components/ui/input";
import { cn } from "@/core/utils/utils";

interface ExpandableSearchProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  buttonPosition?: "left" | "right";
}

export const ExpandableSearch = ({
  value = "",
  onChange,
  placeholder = "Search...",
  className,
  onFocus,
  onBlur,
  buttonPosition = "left",
}: ExpandableSearchProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentValue = onChange ? value : internalValue;
  const setValue = onChange || setInternalValue;

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      // Small delay to allow animation to start before focusing
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isExpanded]);

  const handleExpand = () => {
    setIsExpanded(true);
    onFocus?.();
  };

  const handleClear = () => {
    setValue("");
    setIsExpanded(false);
    onBlur?.();
  };

  const handleButtonClick = () => {
    if (isExpanded) {
      // If expanded, always close regardless of value
      handleClear();
    } else {
      // If collapsed, expand
      handleExpand();
    }
  };

  const handleInputBlur = () => {
    if (!currentValue.trim()) {
      setIsExpanded(false);
    }
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (currentValue.trim()) {
        setValue("");
      } else {
        setIsExpanded(false);
      }
      onBlur?.();
    }
  };

  const buttonElement = useMemo(
    () => (
      <Button
        variant="icon"
        size="iconSm"
        // The trigger is an icon, so it needs a name of its own. Collapsed, the
        // placeholder says what the search is for; expanded, the control closes
        // it, and screen-reader users get told that rather than being told
        // "search" twice.
        aria-label={isExpanded ? "Close search" : placeholder}
        onMouseDown={(e) => {
          e.preventDefault(); // Prevent input from losing focus
          handleButtonClick();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleButtonClick();
          }
        }}
        className="mr-px h-10 w-10 shrink-0 self-center"
      >
        {isExpanded ? (
          <XMarkIcon className="h-4 w-4" />
        ) : (
          <MagnifyingGlassIcon className="h-4 w-4" />
        )}
      </Button>
    ),
    [isExpanded, placeholder, handleButtonClick],
  );

  const inputElement = useMemo(
    () => (
      <div
        className={cn(
          "flex items-center justify-start self-center overflow-hidden transition-all duration-300 ease-in-out",
          {
            "w-0 opacity-0": !isExpanded,
            "w-40 opacity-100": isExpanded,
          },
        )}
      >
        <div className="flex-1 [&>div]:min-h-8">
          <Input
            ref={inputRef}
            value={currentValue}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            // Collapsed, the field is still in the DOM at zero width, so it has
            // to be taken out of the tab order and the a11y tree as well —
            // otherwise a keyboard user lands in a field they cannot see.
            aria-hidden={!isExpanded}
            tabIndex={isExpanded ? undefined : -1}
            className="h-10 min-w-0 px-2 py-0"
          />
        </div>
      </div>
    ),
    [
      isExpanded,
      currentValue,
      placeholder,
      setValue,
      handleInputBlur,
      handleKeyDown,
    ],
  );

  return (
    <div
      className={cn(
        "flex items-center justify-start gap-2 overflow-hidden",
        className,
      )}
    >
      {buttonPosition === "left" ? (
        <>
          {buttonElement}
          {inputElement}
        </>
      ) : (
        <>
          {inputElement}
          {buttonElement}
        </>
      )}
    </div>
  );
};
